'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';

import { OrbLogo } from '@/components/ui/OrbLogo';
import './landing.css';

/* ═══════════════════════════════════════════
   Starfield + Shooting Stars Canvas
   ═══════════════════════════════════════════ */
function Starfield() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const c = canvas;
    const ctx = c.getContext('2d')!;
    let raf: number;

    const palette = ['255,255,255', '200,185,255', '170,145,255', '235,215,255'];

    interface Star {
      x: number; y: number; r: number; o: number;
      sp: number; ph: number; col: string;
    }
    interface Shooter {
      x: number; y: number; vx: number; vy: number;
      life: number; maxLife: number; size: number;
    }

    let stars: Star[] = [];
    const shooters: Shooter[] = [];
    let nextShooter = 1500 + Math.random() * 3000;

    function seed() {
      c.width = window.innerWidth;
      c.height = window.innerHeight * 1.6;
      const n = Math.floor((c.width * c.height) / 14000);
      stars = Array.from({ length: n }, () => ({
        x: Math.random() * c.width,
        y: Math.random() * c.height,
        r: Math.random() * 1.4 + 0.2,
        o: Math.random() * 0.45 + 0.1,
        sp: Math.random() * 0.0005 + 0.00015,
        ph: Math.random() * Math.PI * 2,
        col: palette[Math.floor(Math.random() * palette.length)],
      }));
    }

    function spawnShooter() {
      const startX = Math.random() * c.width * 0.8;
      const startY = Math.random() * c.height * 0.4;
      const angle = Math.PI / 6 + Math.random() * Math.PI / 6;
      const speed = 4 + Math.random() * 4;
      shooters.push({
        x: startX, y: startY,
        vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
        life: 0, maxLife: 40 + Math.random() * 30,
        size: 1 + Math.random() * 1.5,
      });
    }

    let lastTime = 0;
    function draw(t: number) {
      const dt = t - lastTime;
      lastTime = t;
      ctx.clearRect(0, 0, c.width, c.height);

      for (const s of stars) {
        const a = s.o * (Math.sin(t * s.sp + s.ph) * 0.3 + 0.7);
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${s.col},${a})`;
        ctx.fill();
        if (s.r > 0.9) {
          ctx.beginPath();
          ctx.arc(s.x, s.y, s.r * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${s.col},${a * 0.05})`;
          ctx.fill();
        }
      }

      nextShooter -= dt;
      if (nextShooter <= 0) {
        spawnShooter();
        nextShooter = 2000 + Math.random() * 4000;
      }

      for (let i = shooters.length - 1; i >= 0; i--) {
        const sh = shooters[i];
        sh.x += sh.vx;
        sh.y += sh.vy;
        sh.life++;
        const progress = sh.life / sh.maxLife;
        const alpha = progress < 0.3 ? progress / 0.3 : 1 - (progress - 0.3) / 0.7;
        const tailLen = 30 + sh.size * 10;
        const mag = Math.sqrt(sh.vx * sh.vx + sh.vy * sh.vy);
        const grad = ctx.createLinearGradient(
          sh.x, sh.y,
          sh.x - (sh.vx / mag) * tailLen * 0.5,
          sh.y - (sh.vy / mag) * tailLen * 0.5
        );
        grad.addColorStop(0, `rgba(220,210,255,${alpha * 0.8})`);
        grad.addColorStop(1, `rgba(167,139,250,0)`);
        ctx.beginPath();
        ctx.moveTo(sh.x, sh.y);
        ctx.lineTo(sh.x - (sh.vx / mag) * tailLen * 0.5, sh.y - (sh.vy / mag) * tailLen * 0.5);
        ctx.strokeStyle = grad;
        ctx.lineWidth = sh.size;
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(sh.x, sh.y, sh.size * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha * 0.9})`;
        ctx.fill();
        if (sh.life >= sh.maxLife) shooters.splice(i, 1);
      }

      raf = requestAnimationFrame(draw);
    }

    seed();
    raf = requestAnimationFrame(draw);
    const onResize = () => seed();
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, []);

  return <canvas ref={ref} className="stars" aria-hidden="true" />;
}

/* ═══════════════════════════════════════════
   SVG Landscape
   ═══════════════════════════════════════════ */
function Landscape() {
  return (
    <div className="landscape" aria-hidden="true">
      <svg viewBox="0 0 1440 800" preserveAspectRatio="none" className="landscape-svg">
        <defs>
          <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1a1035" stopOpacity="0" />
            <stop offset="30%" stopColor="#2d1b4e" stopOpacity="0.15" />
            <stop offset="50%" stopColor="#4a2c6e" stopOpacity="0.2" />
            <stop offset="70%" stopColor="#6b3a7d" stopOpacity="0.18" />
            <stop offset="85%" stopColor="#8b4a6b" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#a0596b" stopOpacity="0.06" />
          </linearGradient>
          <linearGradient id="horizon-glow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="transparent" />
            <stop offset="55%" stopColor="#9b5a7c" stopOpacity="0.08" />
            <stop offset="70%" stopColor="#c47a6c" stopOpacity="0.06" />
            <stop offset="85%" stopColor="#8b5a6b" stopOpacity="0.04" />
            <stop offset="100%" stopColor="transparent" />
          </linearGradient>
          <linearGradient id="mtn-dist" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#4a3870" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#362858" stopOpacity="0.25" />
          </linearGradient>
          <linearGradient id="mtn-far" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3d2a5c" stopOpacity="0.55" />
            <stop offset="100%" stopColor="#2a1d42" stopOpacity="0.45" />
          </linearGradient>
          <linearGradient id="mtn-mid" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#2d1f47" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#1f1535" stopOpacity="0.65" />
          </linearGradient>
          <linearGradient id="mtn-near" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1a1230" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#0c0816" stopOpacity="0.98" />
          </linearGradient>
          <radialGradient id="haze" cx="50%" cy="55%" r="45%">
            <stop offset="0%" stopColor="#9b6a8a" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#1a1035" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="haze-l" cx="15%" cy="60%" r="30%">
            <stop offset="0%" stopColor="#7a4a7a" stopOpacity="0.06" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
          <radialGradient id="haze-r" cx="85%" cy="55%" r="30%">
            <stop offset="0%" stopColor="#6a4a8a" stopOpacity="0.05" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>
        <rect width="1440" height="800" fill="url(#sky)" />
        <rect width="1440" height="800" fill="url(#horizon-glow)" />
        <rect width="1440" height="800" fill="url(#haze)" />
        <rect width="1440" height="800" fill="url(#haze-l)" />
        <rect width="1440" height="800" fill="url(#haze-r)" />
        <path d="M0,480 C100,440 180,460 280,420 C380,380 450,410 560,370 C670,340 740,380 860,350 C960,325 1040,360 1160,330 C1260,305 1340,340 1440,310 L1440,800 L0,800Z" fill="url(#mtn-dist)" />
        <path d="M0,530 C80,500 140,510 240,480 C340,450 420,475 540,445 C660,420 740,450 860,420 C960,395 1060,430 1180,400 C1280,375 1360,400 1440,380 L1440,800 L0,800Z" fill="url(#mtn-far)" />
        <path d="M0,590 C70,570 130,580 220,555 C320,530 400,560 520,535 C640,510 720,540 840,515 C940,495 1040,525 1160,500 C1260,480 1350,505 1440,490 L1440,800 L0,800Z" fill="url(#mtn-mid)" />
        <path d="M0,660 C20,655 35,645 50,650 C65,643 75,635 90,640 C105,633 115,625 130,630 C150,622 165,635 180,625 C195,618 210,630 230,620 C250,612 265,625 280,615 C300,608 315,620 335,610 C355,603 370,615 390,607 C410,600 425,612 445,604 C465,597 480,608 500,600 C520,593 535,605 555,597 C575,590 590,602 610,594 C630,587 645,598 665,590 C685,583 700,595 720,587 C740,580 755,592 775,584 C795,577 810,588 830,580 C850,573 865,585 885,577 C905,570 920,582 940,574 C960,567 975,578 995,570 C1015,563 1030,575 1050,567 C1070,560 1085,572 1105,564 C1125,557 1140,568 1160,560 C1180,553 1195,565 1215,557 C1235,550 1250,562 1270,554 C1290,547 1305,558 1325,550 C1345,543 1360,555 1380,547 C1400,540 1415,552 1440,544 L1440,800 L0,800Z" fill="url(#mtn-near)" />
      </svg>
    </div>
  );
}

/* ═══════════════════════════════════════════
   Reveal on scroll
   ═══════════════════════════════════════════ */
function Reveal({
  children,
  className = '',
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`rv ${vis ? 'rv-in' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ═══════════════════════════════════════════
   Scroll-driven orb on timeline
   ═══════════════════════════════════════════ */
function ScrollTimeline() {
  const lineRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const orbRef = useRef<HTMLDivElement>(null);
  const orbGlowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const line = lineRef.current;
    const fill = fillRef.current;
    const orb = orbRef.current;
    const orbGlow = orbGlowRef.current;
    if (!line || !fill || !orb || !orbGlow) return;

    let prevLanded = false;

    const update = () => {
      const rect = line.getBoundingClientRect();
      const centerY = window.innerHeight * 0.5;
      const progress = Math.max(0, Math.min(1, (centerY - rect.top) / rect.height));

      fill.style.height = `${progress * 100}%`;

      const y = progress * rect.height;
      orb.style.transform = `translate(-50%, -50%) translateY(${y}px)`;
      orbGlow.style.transform = `translate(-50%, -50%) translateY(${y}px)`;

      // Snap point glow — orb brightens near feature dots
      const dots = [0.167, 0.5, 0.833];
      let nearDot = false;
      for (const d of dots) {
        if (Math.abs(progress - d) < 0.04) {
          nearDot = true;
          break;
        }
      }
      if (nearDot) {
        orb.classList.add('orb-snap');
        orbGlow.classList.add('orb-glow-snap');
      } else {
        orb.classList.remove('orb-snap');
        orbGlow.classList.remove('orb-glow-snap');
      }

      // CTA landing
      const landed = progress > 0.95;
      const ctaSection = document.getElementById('bottom-cta');
      if (landed && !prevLanded) {
        orb.classList.add('orb-landed');
        orbGlow.classList.add('orb-glow-landed');
        ctaSection?.classList.add('cta-lit');
      } else if (!landed && prevLanded) {
        orb.classList.remove('orb-landed');
        orbGlow.classList.remove('orb-glow-landed');
        ctaSection?.classList.remove('cta-lit');
      }
      prevLanded = landed;
    };

    window.addEventListener('scroll', update, { passive: true });
    update();
    return () => window.removeEventListener('scroll', update);
  }, []);

  return (
    <div className="feat-line" ref={lineRef} aria-hidden="true">
      <div className="feat-line-track" />
      <div className="feat-line-fill" ref={fillRef} />
      <div className="orb" ref={orbRef} />
      <div className="orb-glow" ref={orbGlowRef} />
    </div>
  );
}

/* ═══════════════════════════════════════════
   Feature illustrations
   ═══════════════════════════════════════════ */
function AskIllustration() {
  return (
    <div className="feat-illust">
      <div className="illust-pdf">
        <div className="illust-line" style={{ width: '85%' }} />
        <div className="illust-line" style={{ width: '72%' }} />
        <div className="illust-line illust-hl" style={{ width: '90%' }} />
        <div className="illust-line illust-hl" style={{ width: '65%' }} />
        <div className="illust-line" style={{ width: '78%' }} />
        <div className="illust-line" style={{ width: '50%' }} />
      </div>
      <div className="illust-chat">
        <div className="illust-chat-q">What does this mean?</div>
        <div className="illust-chat-a">
          <span className="illust-typing" />
          This explains how the model adjusts weights through backpropagation...
        </div>
      </div>
    </div>
  );
}

function VerifiedIllustration() {
  return (
    <div className="feat-illust">
      <div className="illust-chat illust-chat-verified">
        <div className="illust-chat-a">
          The transformer architecture uses self-attention
          <span className="illust-cite illust-cite-high" title="High confidence">[1]</span>
          {' '}to weight token relationships, replacing recurrence entirely
          <span className="illust-cite illust-cite-high" title="High confidence">[2]</span>
        </div>
        <div className="illust-chat-a illust-chat-a-flagged">
          This approach may reduce training time by 40%
          <span className="illust-cite illust-cite-low" title="Low confidence">[3]</span>
          <span className="illust-flag">⚠ Low confidence — not well supported by source</span>
        </div>
      </div>
      <div className="illust-source-peek">
        <div className="illust-source-label">Source [1] — p.4</div>
        <div className="illust-source-text">&ldquo;Self-attention allows the model to attend to all positions in the previous layer&hellip;&rdquo;</div>
      </div>
    </div>
  );
}

function TopicsIllustration() {
  return (
    <div className="feat-illust">
      {/* Topic with multiple docs */}
      <div className="illust-topic">
        <div className="illust-topic-header">
          <div className="illust-topic-icon">📚</div>
          <div className="illust-topic-name">Machine Learning Research</div>
        </div>
        <div className="illust-topic-docs">
          <div className="illust-topic-doc">Neural Networks.pdf</div>
          <div className="illust-topic-doc">Attention Is All You Need.pdf</div>
          <div className="illust-topic-doc">Scaling Laws.pdf</div>
        </div>
      </div>
      {/* Chat across all docs */}
      <div className="illust-chat">
        <div className="illust-chat-q">Compare how each paper defines &ldquo;emergent abilities&rdquo;</div>
        <div className="illust-chat-a">
          <span className="illust-typing" />
          Across your 3 documents, the term is used differently. In Neural Networks.pdf...
        </div>
      </div>
    </div>
  );
}

function FeatureBlock({
  num,
  label,
  title,
  description,
  illustration: Illust,
  reverse,
}: {
  num: string;
  label: string;
  title: string;
  description: string;
  illustration: React.ComponentType;
  reverse?: boolean;
}) {
  return (
    <div className={`feat-block ${reverse ? 'feat-block-reverse' : ''}`}>
      <Reveal className="feat-block-text">
        <span className="feat-label">{num} {label}</span>
        <h3 className="feat-h3">{title}</h3>
        <p className="feat-desc">{description}</p>
      </Reveal>
      <Reveal className="feat-block-visual" delay={200}>
        <Illust />
      </Reveal>
    </div>
  );
}

/* ═══════════════════════════════════════════
   Page
   ═══════════════════════════════════════════ */
export default function Home() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  return (
    <div className="land">
      <Starfield />
      <Landscape />

      <div className="glow glow-1" aria-hidden="true" />
      <div className="glow glow-2" aria-hidden="true" />

      {/* ── Nav ── */}
      <nav className={`ln-nav${scrolled ? ' ln-scrolled' : ''}`}>
        <span className="ln-logo"><OrbLogo size={28} /> Docuwhy</span>
        <div className="ln-nav-right">
          <Link href="/pricing" className="ln-nav-link">Pricing</Link>
          <Link href="/login" className="ln-nav-link">Log in</Link>
          <Link href="/signup" className="ln-nav-btn">Get Started</Link>
        </div>
      </nav>

      {/* ── Hero ── */}
      <header className="ln-hero">
        <div className="ln-hero-text">
          <h1 className="ln-h1">
            Deeply understand
            <br />
            what you&rsquo;re reading
          </h1>
          <p className="ln-sub">
            Highlight any passage and ask Docuwhy to explain it.
            <br className="hide-mobile" />{' '}
            Every claim is cited and verified.
          </p>
          <div className="ln-cta-row">
            <Link href="/signup" className="ln-btn-primary">
              Get Started <span className="ln-arrow">&rarr;</span>
            </Link>
          </div>
        </div>

        <div className="ln-shot-wrap">
          <div className="ln-shot-glow" aria-hidden="true" />
          <div className="ln-shot-frame">
            <video
              src="/Docuwhy-Marketing-Video.mp4"
              autoPlay
              muted
              loop
              playsInline
              poster="/Docuwhy-gif.gif"
              className="ln-shot-img"
            >
              <img src="/Docuwhy-gif.gif" alt="Docuwhy product demo" />
            </video>
          </div>
        </div>
      </header>

      {/* ── Features ── */}
      <section className="ln-features">
        <Reveal className="ln-features-header">
          <p className="ln-section-label">Features</p>
          <h2 className="ln-section-h2">
            Everything you need to
            <br />
            learn with confidence
          </h2>
        </Reveal>

        <div className="feat-timeline">
          <ScrollTimeline />

          <FeatureBlock
            num="01"
            label="Topics"
            title="Chat across all your documents."
            description="Group PDFs into topics and ask Docuwhy questions that span everything. Compare ideas, find connections, and synthesize across your entire library."
            illustration={TopicsIllustration}
          />
          <FeatureBlock
            num="02"
            label="Ask"
            title="Highlight text. Ask anything."
            description="Select a passage you don't understand, ask Docuwhy to explain it, and get a clear answer in seconds. Powered by Claude, OpenAI, and Gemini."
            illustration={AskIllustration}
            reverse
          />
          <FeatureBlock
            num="03"
            label="Verified"
            title="Every claim backed by your source."
            description="Inline citations link every Docuwhy answer back to the exact page and passage. Low-confidence claims are flagged so you always know what's solid and what's not."
            illustration={VerifiedIllustration}
          />
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="ln-bottom-cta" id="bottom-cta">
        <Reveal>
          <h2 className="ln-cta-h2">Stop assuming. Start knowing.</h2>
          <p className="ln-cta-sub">Free to start. No credit card required.</p>
          <Link href="/signup" className="ln-btn-primary">
            Get Started <span className="ln-arrow">&rarr;</span>
          </Link>
        </Reveal>
      </section>

      {/* ── Footer ── */}
      <footer className="ln-footer">
        <span className="ln-footer-logo"><OrbLogo size={22} /> Docuwhy</span>
        <span className="ln-footer-copy">&copy; 2026 Docuwhy</span>
      </footer>
    </div>
  );
}
